<template>
  <div v-if="isOpen">
    <PmwParagraph size="paragraph-80" weight="light" class="periods--title">
      Black-out
      <PmwInfoTooltip>
        <template #content>
          Período em que a tarifa não vai estar disponível para efetuar uma
          reserva. O dia final que foi configurado não será bloqueado - é o dia
          do checkout.
        </template>
      </PmwInfoTooltip>
    </PmwParagraph>

    <PmwComboInput
      v-model="selectedDates"
      :buttonHasIcon="false"
      :vcalendarOptions="{
        disabledDates
      }"
      label="Datas"
      comboType="date"
      placeholder="dd/mm/aaaa - dd/mm/aaaa"
      buttonLayout="fill"
      dateFormat="dd MMM yyyy"
      size="large"
      range
      @input="addDates"
      @removeItem="removeDate"
    />

    <!-- CHECKBOX DE DEFINIÇÃO DO PERÍODO DE DESTAQUE -->
    <div class="periods--row">
      <PmwInputCheckbox
        v-model="hasHighlightPeriod"
        :checked="hasHighlightPeriod"
        :inputValue="1"
        label="Definir período com destaque"
        name="hasHighlightPeriod"
        class="periods--checkbox"
      />
    </div>

    <PmwParagraph size="paragraph-40" class="periods--paragraph">
      O período escolhido terá destaque sobre os demais na criação do link
      público. <br />
      Servirá para melhorar suas vendas em períodos do mês de baixa procura ou
      que estejam com um desconto maior.
    </PmwParagraph>

    <!-- INPUT DE PERÍODO DE DESTAQUE -->
    <div v-if="hasHighlightPeriod" class="periods--row">
      <PmwFormDatepicker
        v-model="highlightPeriod"
        :error="highlightPeriodError"
        :showErrorFeedbackMessage="showErrorFeedbackMessage"
        feedbackMessage="Selecione até 10 dias"
        label="Selecione a data início e data término"
        placeholder="00/00/0000 - 00/00/0000"
        size="large"
        range
        @input="setHighlightPeriod"
      />

      <!-- RADIOS EXIBIDOS APENAS SE PERÍODO DE DESTAQUE <= 10 DIAS -->
      <div
        v-if="
          differenceInDays(highlightPeriod?.end, highlightPeriod?.start) <= 10
        "
        class="periods--column"
      >
        <!-- DEFINIR COMO PACOTE FECHADO -->
        <div class="periods--column">
          <PmwInputRadio
            v-model="value.pacoteFechado"
            :inputValue="true"
            :checked="value.pacoteFechado"
            label="Definir como pacote fechado"
          />

          <PmwInfoTooltip width="350px" class="periods--column__icon">
            <template #content>
              O pacote fechado considera o dia inicial e o final do período
              selecionado como um todo para aplicação no link público.
            </template>
          </PmwInfoTooltip>
        </div>

        <!-- DEFINIR COMO DIA-A-DIA -->
        <div class="periods--column">
          <PmwInputRadio
            v-model="value.pacoteFechado"
            :inputValue="false"
            :checked="!value.pacoteFechado"
            label="Definir como dia-a-dia"
          />
          <PmwInfoTooltip width="280px" class="periods--column__icon">
            <template #content>
              O período dia-a-dia irá buscar dentro dos dias a melhor data para
              aplicar no link público.
            </template>
          </PmwInfoTooltip>
        </div>
      </div>
    </div>

    <PmwAlertModal
      v-model="showPeriodModal"
      cancelButtonText="Voltar e ajustar"
      confirmButtonText=""
      cancelButtonColor="blue-100"
      text="O período em destaque precisa ser de no máximo 10 dias. Ajuste o período selecionado e tente novamente."
      @alert-modal-cancel="fixHighlightPeriod"
    />
  </div>
</template>

<script>
import { formatISO, differenceInDays } from 'date-fns'
import PmwComboInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwComboInput'
import PmwInfoTooltip from '@solucoes/components-lib-tmp/src/components/patterns/PmwInfoTooltip'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import PmwFormDatepicker from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormDatepicker'
import PmwInputCheckbox from '@solucoes/components-lib-tmp/src/components/elements/PmwInputCheckbox'
import PmwInputRadio from '@solucoes/components-lib-tmp/src/components/elements/PmwInputRadio'
import PmwAlertModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwAlertModal'

export default {
  name: 'PeriodsForm',

  components: {
    PmwComboInput,
    PmwInfoTooltip,
    PmwParagraph,
    PmwFormDatepicker,
    PmwInputCheckbox,
    PmwInputRadio,
    PmwAlertModal
  },

  props: {
    value: {
      type: Object,
      default: () => {}
    },

    isOpen: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedDates: [],

      hasHighlightPeriod: false,

      highlightPeriod: null,

      showPeriodModal: false,

      showErrorFeedbackMessage: false,

      highlightPeriodError: false
    }
  },

  computed: {
    disabledDates() {
      return this.selectedDates
    }
  },

  watch: {
    /**
     * Se o período de destaque tiver mais do que 10 dias, mostra a modal de erro
     * @param {object} newVal: período selecionado
     */
    highlightPeriod(newVal) {
      if (newVal !== null) {
        if (differenceInDays(newVal.end, newVal.start) > 10) {
          this.showPeriodModal = true
        } else {
          this.showErrorFeedbackMessage = false
          this.highlightPeriodError = false
        }
      }
    },

    /**
     * Limpa as datas selecionadas no período de destaque ao desmarcar a checkbox
     * @param {boolean} newVal: valor da checkbox
     */
    hasHighlightPeriod(newVal) {
      if (newVal === false) {
        this.highlightPeriod = null
        this.value.dataInicioPeriodoDestaque = null
        this.value.dataFimPeriodoDestaque = null
        this.value.pacoteFechado = false
        this.showErrorFeedbackMessage = false
        this.highlightPeriodError = false
      }
    },

    'value.dataInicioPeriodoDestaque': {
      handler(newVal) {
        if (newVal) {
          this.hasHighlightPeriod = true

          this.highlightPeriod = {
            start: new Date(newVal),
            end: new Date(this.value.dataFimPeriodoDestaque)
          }
        }
      },
      immediate: true
    },

    'value.periodosBlackout': {
      handler(savedDates) {
        if (savedDates && this.selectedDates.length === 0) {
          this.selectedDates = savedDates.map((item) => {
            return {
              start: new Date(item.dataInicio),
              end: new Date(item.dataFim)
            }
          })
        }
      },
      immediate: true
    }
  },

  methods: {
    /** Limpa o campo de período de destaque e mostra a mensagem de erro */
    fixHighlightPeriod() {
      this.highlightPeriod = null
      this.highlightPeriodError = true
      this.showErrorFeedbackMessage = true
    },

    /**
     * Passa as datas do período de destaque para o objeto com os dados
     * @param {object} event: datas selecionadas
     */
    setHighlightPeriod(event) {
      if (event) {
        this.value.dataInicioPeriodoDestaque = formatISO(event.start)
        this.value.dataFimPeriodoDestaque = formatISO(event.end)
      } else {
        this.value.dataInicioPeriodoDestaque = null
        this.value.dataFimPeriodoDestaque = null
      }
    },

    /**
     * Adiciona as datas selecionadas ao value
     * @param {array} event: datas selecionadas
     */
    addDates(event) {
      this.value.periodosBlackout = event.map((item) => {
        return {
          dataInicio: formatISO(item.start),
          dataFim: formatISO(item.end)
        }
      })
    },

    /** Atualiza o value após a remoção de uma data */
    removeDate() {
      this.value.periodosBlackout = this.selectedDates.map((item) => {
        return {
          dataInicio: formatISO(item.start),
          dataFim: formatISO(item.end)
        }
      })
    },

    differenceInDays
  }
}
</script>

<style lang="scss" scoped>
.periods {
  &--title {
    margin: 25px 0 35px;
  }

  &--row {
    gap: 30px;
    display: flex;
    align-items: flex-end;
    margin-top: 35px;
    margin-bottom: 27px;
  }

  &--range-input {
    width: 262px;
  }

  &--button {
    width: 118px;
    height: 30px;
  }

  &--added-dates {
    border: $pmw-gray-border solid 1px;
    padding: 10px 15px;
    width: fit-content;
    display: inline-flex;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  &--remove-button {
    border: none;
    background: inherit;
    margin-left: 10px;
    width: 12px;
    height: 12px;
    &__icon {
      width: 12px;
      height: 12px;
      color: $pmw-gray-dark;
      &:hover {
        color: pmw-colors('red-100');
      }
    }
  }

  &--row {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 24px;
    margin-bottom: 32px;
  }

  &--paragraph {
    color: pmw-colors('grey-70');
    margin-top: -14px;
    margin-bottom: 24px;
  }

  &--column {
    display: inline-flex;
    gap: 5px;
    align-items: center;

    &__icon {
      color: $pmw-gray-dark;
    }
  }

  &--checkbox {
    width: 435px;
    max-width: 435px;
  }
}

::v-deep {
  .pmw-input-radio {
    input {
      &:checked ~ .pmw-input-radio--fake-radio {
        border-color: pmw-colors('blue-100');

        &:before {
          background-color: pmw-colors('blue-100');
        }
      }
    }
  }
}
</style>
